import { Injectable, inject } from '@angular/core';
import { Observable, filter, tap } from 'rxjs';
import { SnackbarService } from './snackbar.service';
import { EditVideoComponent } from '../../video/modals/edit-video/edit-video.component';
import { ResourceFacade } from '~backoffice/app/~store/facades/resource.facade';
import { VideoService } from '~core/services/video.service';
import { GlobalFacade } from '~backoffice/app/~store/facades/global.facade';
import { DialogService } from '~core/services/dialog.service';
import { KalturaVideo, Video } from '~core/models/core.models';
import { DeleteConfirmComponent } from '../components/modals/delete-confirm/delete-confirm.component';
import { ShareComponent } from '../components/share/share.component';
import { ContentService } from '~core/services/content.service';
import { CreateContentParams } from '~core/models/content.models';

@Injectable()
export class VideoManagementService {
  private resourceFacade = inject(ResourceFacade);
  private videoService = inject(VideoService);
  private contentService = inject(ContentService);
  private globalFacade = inject(GlobalFacade);

  constructor(
    private snackBar: SnackbarService,
    private dialogService: DialogService
  ) {}

  edit(video: Video): Observable<unknown> {
    this.globalFacade.setFormViolations([]);
    const dialogRef = this.dialogService.open({
      component: EditVideoComponent,
      config: {
        panelClass: ['mat-dialog-custom-full-screen'],
        disableClose: true,
        height: '100vh',
        width: '100vw',
        maxWidth: '100vw'
      }
    });

    dialogRef.componentInstance.video = video;
    return dialogRef.afterClosed();
  }

  share(video: Video): Observable<unknown> {
    const dialogRef = this.dialogService.open({
      component: ShareComponent,
      config: {
        panelClass: ['mat-dialog-custom'],
        disableClose: true,
        width: '640px'
      }
    });

    dialogRef.componentInstance.video = video;
    return dialogRef.afterClosed();
  }

  delete(video: Video): Observable<unknown> {
    const delete$ = this.videoService.delete(video.id);
    const dialogRef = this.dialogService.open({
      component: DeleteConfirmComponent,
      config: {
        panelClass: ['mat-dialog-custom'],
        disableClose: true
      }
    });

    dialogRef.componentRef?.setInput('removeObservable$', delete$);
    dialogRef.componentRef?.setInput('wordEntry', video.name);

    return dialogRef.afterClosed().pipe(
      filter(Boolean),
      tap({
        next: () => {
          this.resourceFacade.getData({ refresh: true });
          this.snackBar.open({
            text: 'Vidéo supprimée',
            type: 'success'
          });
        }
      })
    );
  }

  importMedia(item: KalturaVideo): Observable<unknown> {
    this.globalFacade.setFormViolations([]);
    const dialogRef = this.dialogService.open({
      component: EditVideoComponent,
      config: {
        panelClass: ['mat-dialog-custom-full-screen'],
        disableClose: true,
        height: '100vh',
        width: '100vw',
        maxWidth: '100vw'
      }
    });

    dialogRef.componentInstance.media = item;
    return dialogRef.afterClosed().pipe(
      filter(Boolean),
      tap({
        next: () => {
          this.resourceFacade.getData({ refresh: true });
          this.snackBar.open({
            text: 'Vidéo importée',
            type: 'success'
          });
        }
      })
    );
  }

  publishMultiple(
    videos: CreateContentParams[]
  ): Observable<unknown> | undefined {
    if (videos.length === 0) {
      return;
    }

    const request = { contents: videos };

    return this.contentService.createFromBulk(request).pipe(
      tap({
        next: results => {
          this.resourceFacade.getData({ refresh: true });
          this.snackBar.open({
            text: `vidéo importée(s) avec succès`,
            type: 'success'
          });
        }
      })
    );
  }

  publish(video: Video): Observable<Video> {
    const newData = {
      published: true
    };
    return this.videoService.update(video.id, newData).pipe(
      tap({
        next: () => {
          this.resourceFacade.patchItem({
            item: video,
            newData
          });
          this.snackBar.open({
            text: 'Statut de publication mis à jour',
            type: 'success'
          });
        }
      })
    );
  }

  unpublish(video: Video): Observable<unknown> {
    const delete$ = this.videoService.update(video.id, {
      published: false
    });
    const dialogRef = this.dialogService.open({
      component: DeleteConfirmComponent,
      config: {
        panelClass: ['mat-dialog-custom'],
        disableClose: true
      }
    });

    dialogRef.componentRef?.setInput('removeObservable$', delete$);
    dialogRef.componentRef?.setInput(
      'title',
      'Voulez-vous dépublier cette vidéo ?'
    );
    dialogRef.componentRef?.setInput(
      'instruction',
      `Veuillez saisir le texte ci-dessous pour confirmer la dépublication`
    );
    dialogRef.componentRef?.setInput(
      'wordEntry',
      'Je confirme la dépublication'
    );
    dialogRef.componentRef?.setInput('action', 'Dépublier');

    return dialogRef.afterClosed().pipe(
      filter(Boolean),
      tap({
        next: () => {
          this.resourceFacade.patchItem({
            item: video,
            newData: {
              published: false
            }
          });
          this.snackBar.open({
            text: 'Programme dépublié',
            type: 'success'
          });
        }
      })
    );
  }

  program(video: Video): Observable<Video> {
    const newData = {
      scheduledPublication: true
    };
    return this.videoService.update(video.id, newData).pipe(
      tap({
        next: () => {
          this.resourceFacade.patchItem({
            item: video,
            newData
          });
          this.snackBar.open({
            text: 'Vidéo programmé',
            type: 'success'
          });
        }
      })
    );
  }

  deprogram(video: Video): Observable<unknown> {
    const delete$ = this.videoService.update(video.id, {
      scheduledPublication: false
    });
    const dialogRef = this.dialogService.open({
      component: DeleteConfirmComponent,
      config: {
        panelClass: ['mat-dialog-custom'],
        disableClose: true
      }
    });

    dialogRef.componentRef?.setInput('removeObservable$', delete$);
    dialogRef.componentRef?.setInput(
      'title',
      'Voulez-vous déprogrammer cette vidéo ?'
    );
    dialogRef.componentRef?.setInput(
      'instruction',
      `Veuillez saisir le texte ci-dessous pour confirmer la déprogrammation`
    );
    dialogRef.componentRef?.setInput(
      'wordEntry',
      'Je confirme la déprogrammation'
    );
    dialogRef.componentRef?.setInput('action', 'Déprogrammer');

    return dialogRef.afterClosed().pipe(
      filter(Boolean),
      tap({
        next: () => {
          this.resourceFacade.patchItem({
            item: video,
            newData: {
              scheduledPublication: false
            }
          });
          this.snackBar.open({
            text: 'Programme dépublié',
            type: 'success'
          });
        }
      })
    );
  }

  togglePublishedState(video: Video, oldState?: boolean): Observable<unknown> {
    return oldState ? this.unpublish(video) : this.publish(video);
  }
}
